import { useState, useEffect, useMemo } from 'react';
import { FiChevronDown, FiChevronUp, FiCalendar } from 'react-icons/fi';
import OrganizationModal from '../Modal/OrganizationModal';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SearchBar from 'components/shared/SearchBar';

interface OrganizationFiltersProps {
  setRefreshMode: (value: string) => void;
  setFilters: (filters: any) => void;
  filters: {
    organization: string;
    subscriptionType: string;
    billingPeriod: string;
    paymentStatus: string;
    startDate: Date | null;
    endDate: Date | null;
    search: string;
  };
}

interface DropdownOption {
  id: string | number;
  label: string;
}

export default function OrganizationFilters({ setRefreshMode, setFilters, filters }: OrganizationFiltersProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Ensure filters object is properly initialized
  const safeFilters = filters || {
    organization: '',
    subscriptionType: '',
    billingPeriod: '',
    paymentStatus: '',
    startDate: null,
    endDate: null,
    search: ''
  };


  const subscriptionTypes: DropdownOption[] = [
    { id: 'free-trial', label: 'Free Trial' },
    { id: 'basic', label: 'Basic' },
    { id: 'premium', label: 'Premium' },
    { id: 'enterprise', label: 'Enterprise' }
  ];

  const billingPeriods: DropdownOption[] = [
    { id: 'monthly', label: 'Monthly' },
    { id: 'quarterly', label: 'Quarterly' },
    { id: 'yearly', label: 'Yearly' }
  ];

  const paymentStatuses: DropdownOption[] = [
    { id: 'paid', label: 'Paid' },
    { id: 'pending', label: 'Pending' },
    { id: 'overdue', label: 'Overdue' }
  ];

  const handleDropdownClick = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const handleOptionSelect = (dropdownName: string, value: string) => {
    setFilters((prev: any) => {
      // Ensure prev is defined and is an object
      const currentFilters = prev && typeof prev === 'object' ? prev : {};
      return { ...currentFilters, [dropdownName]: value };
    });
    setOpenDropdown(null);
  };

  const clearFilters = () => {
    // Ensure we're working with a safe filters object
    const defaultFilters = {
      organization: '',
      subscriptionType: '',
      billingPeriod: '',
      paymentStatus: '',
      startDate: null,
      endDate: null,
      search: ''
    };
    
    setFilters(defaultFilters);
    setSearchTerm(''); // Also reset the local search term
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters((prev: any) => {
      // Ensure prev is defined and is an object
      const currentFilters = prev && typeof prev === 'object' ? prev : {};
      return { ...currentFilters, search: value };
    });
  };

  useEffect(() => {
    setFilters((prev: any) => {
      // Ensure prev is defined and is an object
      const currentFilters = prev && typeof prev === 'object' ? prev : {};
      return { ...currentFilters, search: searchTerm };
    });
  }, [searchTerm]);

  // Check if there are any active filters (excluding empty strings and null values)
  const hasActiveFilters = useMemo(() => {
    // Additional safety check to ensure safeFilters is defined
    if (!safeFilters) return false;
    
    return (
      (safeFilters.organization && safeFilters.organization !== '') ||
      (safeFilters.subscriptionType && safeFilters.subscriptionType !== '') ||
      (safeFilters.billingPeriod && safeFilters.billingPeriod !== '') ||
      (safeFilters.paymentStatus && safeFilters.paymentStatus !== '') ||
      (safeFilters.startDate !== null && safeFilters.startDate !== undefined) ||
      (safeFilters.endDate !== null && safeFilters.endDate !== undefined) ||
      (safeFilters.search && safeFilters.search !== '')
    );
  }, [safeFilters]);

  const Dropdown = ({ name, options, label }: { name: string; options: DropdownOption[]; label: string }) => (
    <div className="relative">
      <button
        onClick={() => handleDropdownClick(name)}
        className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
        style={{
          minWidth: '140px',
          maxWidth: '140px',
        }}
      >
        <span className="truncate mr-2">
          {(safeFilters[name as keyof typeof safeFilters] && safeFilters[name as keyof typeof safeFilters] !== '') 
            ? safeFilters[name as keyof typeof safeFilters]?.toString() 
            : label}
        </span>
        {openDropdown === name ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
      </button>

      {openDropdown === name && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpenDropdown(null)}
          />
          <div className="absolute top-full left-0 mt-2 w-48 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
            <button
              disabled
              className="w-full text-left px-4 py-2 text-gray-500 bg-[#2A2A2A] cursor-default truncate opacity-50"
            >
              {label}
            </button>
            
            <div className="border-b border-white/[0.08] my-1"></div>
            
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(name, option.label)}
                className="w-full text-left px-4 py-2 text-gray-400 hover:bg-[#3a3a3a33] hover:text-white truncate"
                title={option.label}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <SearchBar
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search organizations..."
          />
          
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
              style={{
                minWidth: '140px',
                maxWidth: '200px',
              }}
            >
              <div className="flex items-center gap-2 truncate">
                <FiCalendar className="flex-shrink-0" />
                <span className="truncate">
                  {(safeFilters.startDate && safeFilters.endDate && safeFilters.startDate !== null && safeFilters.endDate !== null)
                    ? `${new Date(safeFilters.startDate).toLocaleDateString()} - ${new Date(safeFilters.endDate).toLocaleDateString()}`
                    : 'Select Duration'
                  }
                </span>
              </div>
            </button>
            
            {showDatePicker && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDatePicker(false)}
                />
                <div className="absolute top-full left-0 mt-2 z-20 bg-[#2A2A2A] rounded-lg p-4 border border-white/[0.08]">
                  <DatePicker
                    selectsRange={true}
                    startDate={safeFilters.startDate}
                    endDate={safeFilters.endDate}
                    onChange={(update: [Date | null, Date | null]) => {
                      setFilters((prev: any) => {
                        // Ensure prev is defined and is an object
                        const currentFilters = prev && typeof prev === 'object' ? prev : {};
                        return {
                          ...currentFilters,
                          startDate: update[0],
                          endDate: update[1]
                        };
                      });
                      if (update[0] && update[1]) {
                        setShowDatePicker(false);
                      }
                    }}
                    inline
                  />
                </div>
              </>
            )}
          </div>
          
          <Dropdown
            name="subscriptionType"
            options={subscriptionTypes}
            label="Subscription type"
          />
          
          <Dropdown
            name="billingPeriod"
            options={billingPeriods}
            label="Billing Period"
          />
          
          <Dropdown
            name="paymentStatus"
            options={paymentStatuses}
            label="Payment Status"
          />

          {hasActiveFilters && (
            <button 
              onClick={clearFilters}
              className="text-red-400 hover:text-red-300"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white">
            ↓ Export
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 rounded-md px-4 py-2 text-white"
          >
            + Add Organization
          </button>
        </div>
      </div>

      <OrganizationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode="create"
        setRefreshMode={setRefreshMode}
      />
    </>
  );
} 